import React, { useEffect } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Modal from "components/Modal";
import Shadow from "components/Shadow";
import Button from "components/Button";
import ImgIcon from "components/imgIcon";
import Separator from "components/Separator";
import InputLabel from "components/InputLabel";
import LoadingBox from "components/LoadingBox";
import Permissions from "components/Permissions";
import ConfirmAlert from "components/ConfirmAlert";
import DeleteAlertComponent from "components/DeleteAlertComponent";

import { useForm, Controller } from "react-hook-form";

import { useCreateRole, useDeleteRole, useUpdateRole } from "clients/apis";
import { sizes, colors, strings } from "assets/constants";
import { icons } from "assets/icons";
import { alertPermission, isEqualArrays, sn } from "utils";
import { useChmod, useFlag } from "hooks";
import CheckBox from "./CheckBox";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";

const RoleCard: React.FC<{ role: RoleT }> = ({ role }) => {
  const { chmod } = useChmod();
  const auth = useRecoilValue(authAtom);

  const {
    control,
    reset,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<{ force: 0 | 1; name: string; permissions: Array<number> }>({
    defaultValues: {
      name: role?.name || "",
      permissions: role?.permissions?.map((e) => e?.id) || [],
      force: 0,
    },
  });

  useEffect(() => {
    if (role) {
      setValue("name", role?.name);
      setValue("permissions", role?.permissions?.map((e) => e?.id) || []);
    }
  }, [role]);

  const { flg: edit, on: goToEdit, off: closeEdit } = useFlag();
  const { flg: remove, on: goToRemove, off: closeRemove } = useFlag();

  const createRoleMut = useCreateRole();
  const updateRoleMut = useUpdateRole();
  const deleteRoleMut = useDeleteRole();

  const _onHide = () => {
    reset();
    closeEdit();
  };

  const _onSubmit = () =>
    role?.id
      ? chmod("company", "PUT")
        ? _onUpdate({ id: role.id, ...getValues() })
        : alertPermission
      : chmod("company", "POST")
      ? _onCreate({ ...getValues(), company_id: auth?.company?.id })
      : alertPermission;

  const _onCreate = (data: CreateRoleReqT) =>
    createRoleMut.mutate(data, { onSuccess: _onHide });

  const _onUpdate = (data: UpdateRoleReqT) => {
    // const prevName = role?.name;
    // const prevPermissions = role?.permissions?.map((e) => e?.id);
    // if (
    //   data?.name === prevName &&
    //   isEqualArrays(prevPermissions, data?.permissions)
    // ) {
    //   _onHide();
    // } else {
    //   updateRoleMut.mutate(data, { onSuccess: _onHide });
    // }
    updateRoleMut.mutate(data, { onSuccess: _onHide });
  };

  const _onDeleteConfirm = (close: () => void) => {
    close();
    reset();
    deleteRoleMut.mutate({ id: role?.id });
  };

  const _renderContent = () => {
    if (!role) {
      return (
        <TouchableOpacity
          onPress={chmod("company", "POST") ? goToEdit : alertPermission}
          activeOpacity={sizes.tapOpacity}
          style={styles.plusContainer}
        >
          <ImgIcon icon={icons._plus} size={sn(25, 28, 30, 30)} />
        </TouchableOpacity>
      );
    }

    return (
      <>
        <View style={styles.header}>
          <ImgIcon size={25} icon={icons._role} />
          <Text style={{ lineHeight: 20 }} numberOfLines={1} weight="bold">
            {role?.name}
          </Text>
        </View>
        <View style={styles.footer}>
          {role?.type === "general" ? (
            <View>
              <Text
                align="center"
                size={"xs"}
                weight="bold"
                color={colors.text.dark}
              >
                نقش عمومی
              </Text>

              <Button
                height={50}
                title={{ value: "مجوزها", color: colors.info, weight: "bold" }}
                bgColor={colors.transparent}
                onPress={goToEdit}
                mb={8}
              />
            </View>
          ) : (
            <>
              <TouchableOpacity
                onPress={goToEdit}
                activeOpacity={sizes.tapOpacity}
                style={styles.actions}
              >
                <Icon
                  size={18}
                  type="simpleLine"
                  name="pencil"
                  color={colors.theme}
                />
              </TouchableOpacity>
              <Separator axis="y" size={"90%"} />
              <TouchableOpacity
                onPress={
                  chmod("company", "DELETE") ? goToRemove : alertPermission
                }
                activeOpacity={sizes.tapOpacity}
                style={styles.actions}
              >
                <Icon
                  size={18}
                  type="simpleLine"
                  name="trash"
                  color={colors.danger}
                />
              </TouchableOpacity>
            </>
          )}
        </View>
      </>
    );
  };

  return (
    <>
      <Shadow style={styles.shadowCard}>{_renderContent()}</Shadow>

      <Modal visible={edit} close={closeEdit}>
        {role?.type !== "general" && (
          <>
            <InputLabel label={strings.title} />
            <Controller
              control={control}
              name="name"
              rules={{ required: { value: true, message: strings.fill_input } }}
              render={({ field: { value, onChange } }) => (
                <Input
                  value={value}
                  onChangeText={onChange}
                  imgIcon={{ icon: icons._role }}
                  placeholder={strings.insert_role_title}
                  error={errors?.name?.message}
                />
              )}
            />
          </>
        )}

        <View style={{ marginVertical: 15 }}>
          {role?.id && (
            <Controller
              control={control}
              name="force"
              render={({ field: { value, onChange } }) => {
                return (
                  <CheckBox
                    value={value}
                    onChange={onChange}
                    title="مجوزهای نقش برای کارمندان این نقش اعمال شود"
                  />
                );
              }}
            />
          )}
        </View>

        <InputLabel label={strings.permissions} />
        <Controller
          control={control}
          name="permissions"
          render={({ field: { value, onChange } }) => (
            <Permissions
              editable={role?.type !== "general"}
              defaults={value}
              onChange={onChange}
            />
          )}
        />

        {role?.type !== "general" && (
          <Button
            title={{ value: strings.confirm }}
            mt={sizes.space}
            onPress={handleSubmit(_onSubmit)}
            loading={createRoleMut.isLoading || updateRoleMut.isLoading}
          />
        )}
      </Modal>

      <ConfirmAlert
        force
        show={remove}
        hide={closeRemove}
        renderContent={(close) => (
          <DeleteAlertComponent
            title={`آیا میخواهید نقش ${role?.name} را حذف کنید؟ `}
            remove={() => _onDeleteConfirm(close)}
            cancel={close}
          />
        )}
      />

      {deleteRoleMut.isLoading && <LoadingBox />}
    </>
  );
};

export default RoleCard;

const styles = StyleSheet.create({
  shadowCard: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
    alignItems: "center",
    borderRadius: sizes.radius.xl,
    backgroundColor: colors.white,
    justifyContent: "space-evenly",
  },

  header: {
    flex: 1,
    padding: sizes.space,
    alignItems: "center",
    justifyContent: "center",
  },

  footer: {
    width: "100%",
    height: sn(40, 60, 60, 60),
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row-reverse",
  },

  actions: { flex: 1, alignItems: "center", justifyContent: "center" },

  plusContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },

  row: { flexDirection: "row-reverse", alignSelf: "center" },
});
